<p-toast></p-toast>
<p-progressBar mode="indeterminate" class="m-2" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
    <app-page-hero eyebrow="Reports" title="Balance Sheet" subtitle="Review assets, liabilities, equity, and accounting equation checks.">
        <div actions class="report-actions">
            <p-button label="Generate" icon="pi pi-refresh" (onClick)="loadBalanceSheetData({})"></p-button>
            <p-button label="Export" icon="pi pi-file-excel" severity="success" (onClick)="exportExcel()"></p-button>
            <p-button label="Download PDF" icon="pi pi-file-pdf" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('download')" (onClick)="printReport(true)"></p-button>
            <p-button label="Print" icon="pi pi-print" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('print')" (onClick)="printReport()"></p-button>
            <app-report-email reportName="Balance Sheet" [rows]="assetItems.concat(liabilityItems, equityItems)"></app-report-email>
        </div>
    </app-page-hero>

    <app-page-form-section icon="pi pi-filter" title="Report Filters" subtitle="Choose the date range and comparison period.">
        <div class="report-filter-grid">
            <p-iftalabel>
                <p-datePicker inputId="startDate" [(ngModel)]="startDate" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full" (onSelect)="loadBalanceSheetData({})"></p-datePicker>
                <label for="startDate">Start Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-datePicker inputId="endDate" [(ngModel)]="endDate" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full" (onSelect)="loadBalanceSheetData({})"></p-datePicker>
                <label for="endDate">End Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-select inputId="periodType" [options]="periodOptions" [(ngModel)]="selectedPeriodType" optionLabel="name" optionValue="value"
                          (onChange)="onPeriodTypeChange()" placeholder="Select period type" styleClass="w-full"></p-select>
                <label for="periodType">Period Type</label>
            </p-iftalabel>
            <p-iftalabel>
                <app-logistics-project-lookup inputId="logisticsProjectId" [(ngModel)]="selectedLogisticsProjectId" (ngModelChange)="loadBalanceSheetData({})"></app-logistics-project-lookup>
                <label for="logisticsProjectId">Logistics Project</label>
            </p-iftalabel>
        </div>
    </app-page-form-section>

    <app-page-data-panel *ngIf="diagnostics.length" title="Report Diagnostics" [resultText]="diagnostics.length + ' checks'">
        <p-table [value]="diagnostics" styleClass="p-datatable-gridlines" [tableStyle]="{ 'min-width': '60rem' }">
            <ng-template pTemplate="header">
                <tr>
                    <th>Severity</th>
                    <th>Code</th>
                    <th>Message</th>
                    <th>Reference</th>
                    <th class="text-right">Amount</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-diagnostic>
                <tr>
                    <td>{{ diagnostic.severity }}</td>
                    <td>{{ diagnostic.code }}</td>
                    <td>{{ diagnostic.message }}</td>
                    <td>{{ diagnostic.referenceNumber || diagnostic.referenceId || '-' }}</td>
                    <td class="text-right">{{ diagnostic.amount ?? '-' }}</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>

    <div class="grid">
        <div class="col-12 md:col-4">
            <p-card header="Total Assets" styleClass="text-center report-metric-card">
                <div class="text-2xl font-bold" [ngClass]="{'text-green-600': totalAssets > 0, 'text-red-600': totalAssets < 0}">
                    {{ totalAssets | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </div>
            </p-card>
        </div>
        <div class="col-12 md:col-4">
            <p-card header="Total Liabilities" styleClass="text-center report-metric-card">
                <div class="text-2xl font-bold" [ngClass]="{'text-red-600': totalLiabilities > 0, 'text-green-600': totalLiabilities < 0}">
                    {{ totalLiabilities | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </div>
            </p-card>
        </div>
        <div class="col-12 md:col-4">
            <p-card header="Total Equity" styleClass="text-center report-metric-card">
                <div class="text-2xl font-bold" [ngClass]="{'text-green-600': totalEquity > 0, 'text-red-600': totalEquity < 0}">
                    {{ totalEquity | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </div>
            </p-card>
        </div>
    </div>

<!-- Balance Sheet Equation Check -->
<app-page-data-panel title="Balance Sheet Equation Check"
    [resultText]="balancedPeriodCount + ' of ' + selectedPeriods.length + ' periods balanced'">
    <section class="equation-check-shell" [class.equation-balanced]="allPeriodsBalanced" [class.equation-unbalanced]="!allPeriodsBalanced">
        <div class="equation-status-banner">
            <div class="equation-status-icon" aria-hidden="true">
                <i class="pi" [ngClass]="allPeriodsBalanced ? 'pi-check-circle' : 'pi-exclamation-triangle'"></i>
            </div>
            <div class="equation-status-copy">
                <span class="equation-eyebrow">Accounting equation</span>
                <h3>{{ allPeriodsBalanced ? 'Balance sheet is balanced' : 'Balance sheet needs attention' }}</h3>
                <p *ngIf="allPeriodsBalanced">Assets equal liabilities plus equity in every selected period.</p>
                <p *ngIf="!allPeriodsBalanced">
                    {{ selectedPeriods.length - balancedPeriodCount }}
                    {{ selectedPeriods.length - balancedPeriodCount === 1 ? 'period is' : 'periods are' }} out of balance.
                </p>
            </div>

            <div class="latest-equation" *ngIf="latestPeriod as period">
                <div class="latest-equation-heading">
                    <span>Latest period</span>
                    <strong>{{ period }}</strong>
                </div>
                <div class="latest-equation-values" aria-label="Latest balance sheet equation">
                    <div class="equation-amount">
                        <span>Assets</span>
                        <strong>{{ getTotalForCategory('asset', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}</strong>
                    </div>
                    <span class="equation-operator">=</span>
                    <div class="equation-amount">
                        <span>Liabilities</span>
                        <strong>{{ getTotalForCategory('liability', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}</strong>
                    </div>
                    <span class="equation-operator">+</span>
                    <div class="equation-amount">
                        <span>Equity</span>
                        <strong>{{ getTotalForCategory('equity', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}</strong>
                    </div>
                </div>
                <div class="equation-difference" [class.difference-ok]="isPeriodBalanced(period)" [class.difference-error]="!isPeriodBalanced(period)">
                    <span>Difference</span>
                    <strong>{{ getEquationDifference(period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}</strong>
                </div>
            </div>
        </div>

        <div class="equation-period-table-wrap">
            <table class="equation-period-table">
                <thead>
                    <tr>
                        <th>Period</th>
                        <th class="text-right">Assets</th>
                        <th class="text-right">Liabilities</th>
                        <th class="text-right">Equity</th>
                        <th class="text-right">Difference</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let period of selectedPeriods" [class.period-out-of-balance]="!isPeriodBalanced(period)">
                        <td><strong>{{ period }}</strong></td>
                        <td class="text-right">{{ getTotalForCategory('asset', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}</td>
                        <td class="text-right">{{ getTotalForCategory('liability', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}</td>
                        <td class="text-right">{{ getTotalForCategory('equity', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}</td>
                        <td class="text-right equation-period-difference" [class.difference-ok-text]="isPeriodBalanced(period)" [class.difference-error-text]="!isPeriodBalanced(period)">
                            {{ getEquationDifference(period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}
                        </td>
                        <td>
                            <span class="period-status" [class.status-balanced]="isPeriodBalanced(period)" [class.status-unbalanced]="!isPeriodBalanced(period)">
                                <i class="pi" [ngClass]="isPeriodBalanced(period) ? 'pi-check' : 'pi-times'"></i>
                                {{ isPeriodBalanced(period) ? 'Balanced' : 'Out of balance' }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</app-page-data-panel>

<div class="balance-sheet-sections">
<div class="balance-sheet-column">
<!-- Assets Table -->
<app-page-data-panel title="Assets">
    <p-table [value]="assetItems" dataKey="accountId" [resizableColumns]="true" styleClass="accounting-tree-table"
             [rows]="10" [paginator]="true" [totalRecords]="totalAssetRecords" [showCurrentPageReport]="true"
             currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries">
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="accountCode" class="report-account-column">Account <p-sortIcon field="accountCode"></p-sortIcon></th>
                <th *ngFor="let period of selectedPeriods" class="text-right" [pSortableColumn]="'balances.' + period">
                    {{ period }} <p-sortIcon [field]="'balances.' + period"></p-sortIcon>
                </th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr [ngClass]="{'report-summary-row': item.isSummaryRow, 'report-root-row': item.level === 0, 'report-direct-row': item.isDirectBalanceRow}">
                <td>
                    <app-accounting-report-tree-cell [accountCode]="item.accountCode" [accountName]="item.accountName"
                        [level]="item.level" [childCount]="item.children?.length || 0" [expanded]="!!item.expanded"
                        [summary]="!!item.isSummaryRow" [direct]="!!item.isDirectBalanceRow" directLabel="Direct balance"
                        (toggled)="toggleNode(item)"></app-accounting-report-tree-cell>
                </td>
                <td *ngFor="let period of selectedPeriods" class="report-value-cell"
                    [ngClass]="{'report-value-credit': getBalanceSide(item.balances[period], item.internalType) === 'CR', 'report-value-zero': getBalanceSide(item.balances[period], item.internalType) === 'ZERO'}">
                    <div class="report-value-stack"><span class="report-value">{{ item.balances[period] | currency:reportCurrencyCode:'symbol':'1.2-2' }}</span><span class="report-balance-side">{{ getBalanceSide(item.balances[period], item.internalType) }}</span></div>
                </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr class="bg-gray-200 font-bold text-lg">
                <td class="report-total-label">Total Assets</td>
                <td *ngFor="let period of selectedPeriods" class="text-right">
                    {{ getTotalForCategory('asset', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

</div>
<div class="balance-sheet-column">
<!-- Liabilities Table -->
<app-page-data-panel title="Liabilities">
    <p-table [value]="liabilityItems" dataKey="accountId" [resizableColumns]="true" styleClass="accounting-tree-table"
             [rows]="10" [paginator]="true" [totalRecords]="totalLiabilityRecords" [showCurrentPageReport]="true"
             currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries">
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="accountCode" class="report-account-column">Account <p-sortIcon field="accountCode"></p-sortIcon></th>
                <th *ngFor="let period of selectedPeriods" class="text-right" [pSortableColumn]="'balances.' + period">
                    {{ period }} <p-sortIcon [field]="'balances.' + period"></p-sortIcon>
                </th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr [ngClass]="{'report-summary-row': item.isSummaryRow, 'report-root-row': item.level === 0, 'report-direct-row': item.isDirectBalanceRow}">
                <td>
                    <app-accounting-report-tree-cell [accountCode]="item.accountCode" [accountName]="item.accountName"
                        [level]="item.level" [childCount]="item.children?.length || 0" [expanded]="!!item.expanded"
                        [summary]="!!item.isSummaryRow" [direct]="!!item.isDirectBalanceRow" directLabel="Direct balance"
                        (toggled)="toggleNode(item)"></app-accounting-report-tree-cell>
                </td>
                <td *ngFor="let period of selectedPeriods" class="report-value-cell"
                    [ngClass]="{'report-value-credit': getBalanceSide(item.balances[period], item.internalType) === 'CR', 'report-value-zero': getBalanceSide(item.balances[period], item.internalType) === 'ZERO'}">
                    <div class="report-value-stack"><span class="report-value">{{ item.balances[period] | currency:reportCurrencyCode:'symbol':'1.2-2' }}</span><span class="report-balance-side">{{ getBalanceSide(item.balances[period], item.internalType) }}</span></div>
                </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr class="bg-gray-200 font-bold text-lg">
                <td class="report-total-label">Total Liabilities</td>
                <td *ngFor="let period of selectedPeriods" class="text-right">
                    {{ getTotalForCategory('liability', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

<!-- Equity Table -->
<app-page-data-panel title="Equity">
    <p-table [value]="equityItems" dataKey="accountId" [resizableColumns]="true" styleClass="accounting-tree-table"
             [rows]="10" [paginator]="true" [totalRecords]="totalEquityRecords" [showCurrentPageReport]="true"
             currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries">
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="accountCode" class="report-account-column">Account <p-sortIcon field="accountCode"></p-sortIcon></th>
                <th *ngFor="let period of selectedPeriods" class="text-right" [pSortableColumn]="'balances.' + period">
                    {{ period }} <p-sortIcon [field]="'balances.' + period"></p-sortIcon>
                </th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr [ngClass]="{'report-summary-row': item.isSummaryRow, 'report-root-row': item.level === 0, 'report-direct-row': item.isDirectBalanceRow}">
                <td>
                    <app-accounting-report-tree-cell [accountCode]="item.accountCode" [accountName]="item.accountName"
                        [level]="item.level" [childCount]="item.children?.length || 0" [expanded]="!!item.expanded"
                        [summary]="!!item.isSummaryRow" [direct]="!!item.isDirectBalanceRow" directLabel="Direct balance"
                        (toggled)="toggleNode(item)"></app-accounting-report-tree-cell>
                </td>
                <td *ngFor="let period of selectedPeriods" class="report-value-cell"
                    [ngClass]="{'report-value-credit': getBalanceSide(item.balances[period], item.internalType) === 'CR', 'report-value-zero': getBalanceSide(item.balances[period], item.internalType) === 'ZERO'}">
                    <div class="report-value-stack"><span class="report-value">{{ item.balances[period] | currency:reportCurrencyCode:'symbol':'1.2-2' }}</span><span class="report-balance-side">{{ getBalanceSide(item.balances[period], item.internalType) }}</span></div>
                </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr class="bg-gray-200 font-bold text-lg">
                <td class="report-total-label">Total Equity</td>
                <td *ngFor="let period of selectedPeriods" class="text-right">
                    {{ getTotalForCategory('equity', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

</div>
</div>
</app-page-shell>
